import styled from 'styled-components';
import {
  navIconStyles,
  navTextStyles,
  sharedNavItemRootStyles,
} from '../styles';
import { DisclosureContent } from 'reakit';
import {
  ColorsV2,
  Flex,
  getToken,
  Icon,
  IconNames,
  useCollapse,
} from '@electricjs/arc';

const StyledCollapsibleSubNav = styled.div`
  ${sharedNavItemRootStyles}
  padding-left: 0;
  padding-right: 0;

  .collapsible-subnav__section {
    cursor: pointer;
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-appearance: none;
    /* stylelint-enable property-no-vendor-prefix */
    display: flex;
    align-items: center;
    background: none;
    border: none;
    text-align: left;
    width: 100%;
    height: 4.5rem;
    padding: 0 1.6rem;
    border-radius: 0.8rem;
    transition: background-color 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      background: rgba(255, 255, 255, 0.1);
    }

    /* Collapsible nav icon */
    ${navIconStyles}

    /* Collapsible nav arrow indicator icon */
    div[data-testid='nav-collapsible-icon-arrow'] {
      margin-left: 2.6rem;
      margin-right: 0;
    }

    .collapsible-subnav__text {
      ${navTextStyles}
      flex-grow: 1;
      height: 4.5rem;
      line-height: 4.5rem;
      color: ${getToken(ColorsV2.WHITE_STATIC)};
    }
  }

  .collapsible-subnav__items {
    margin-left: 1.5rem;

    & > div:first-child {
      transition:
        opacity 200ms ease-in-out,
        transform 200ms ease-in-out;
      opacity: 0;
      transform: translate3d(0, -5%, 0);

      &[data-enter] {
        margin-top: 1rem;
        opacity: 1;
        transform: translate3d(0, 0, 0);
      }
    }

    .nav-item {
      margin-top: 0;
      margin-bottom: 1rem;

      a:first-child {
        margin-top: 0.5rem;
      }

      span {
        font-size: 1.4rem;
        font-weight: 400;
      }
    }
  }
`;

const CollapsedLabel = styled.span`
  color: ${getToken(ColorsV2.WHITE_STATIC)};
  display: block;
  margin-top: 0.6rem;
`;

type SubNavToggleProps = {
  icon?: IconNames;
  text?: string;
  shortText?: string;
  toggle: () => void;
  isVisible: boolean;
};

const SubNavToggle = ({
  icon,
  text,
  shortText,
  toggle,
  isVisible,
}: SubNavToggleProps): React.ReactElement => (
  <button
    type="button"
    data-testid="subnav-toggle"
    className="collapsible-subnav__section"
    id="collapsed-toggle-nav"
    aria-controls="collapsed-panel-nav"
    onClick={toggle}>
    {icon && (
      <Flex
        flexDirection="column"
        alignItems="center"
        data-nav-icon-label-container>
        <Icon icon={icon} color={ColorsV2.WHITE_STATIC} title={text} />
        {(shortText || text) && (
          <CollapsedLabel data-nav-label>{shortText || text}</CollapsedLabel>
        )}
      </Flex>
    )}
    <span className={`collapsible-subnav__text${isVisible ? ' expanded' : ''}`}>
      {text}
    </span>
    <Icon
      icon={isVisible ? 'chevron-up' : 'chevron-down'}
      size="small"
      color={ColorsV2.WHITE_STATIC}
      testId="nav-collapsible-icon-arrow"
    />
  </button>
);

type CollapsibleSubNavProps = {
  children: React.ReactNode;
  icon?: IconNames;
  text?: string;
  shortText?: string;
  hasActiveSubNav?: boolean;
  $toggleSideNav?: () => void;
  $isSideNavCollapsed?: boolean;
};

export const CollapsibleSubNav = ({
  children,
  hasActiveSubNav = false,
  $toggleSideNav,
  $isSideNavCollapsed,
  ...otherProps
}: CollapsibleSubNavProps): React.ReactElement => {
  const disclosure = useCollapse({ visible: hasActiveSubNav });
  const { toggle, visible } = disclosure;
  // This avoids passing the `Collapsible` component to the DOM, which is unused and throws an error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { Collapsible, ...disclosureProps } = disclosure;

  return (
    <StyledCollapsibleSubNav
      className="collapsible-subnav"
      data-testid={'collapsible-subnav'}>
      <SubNavToggle
        {...otherProps}
        toggle={() => {
          toggle();
          if ($isSideNavCollapsed && $toggleSideNav) {
            $toggleSideNav();
          }
        }}
        isVisible={visible}
      />
      <div
        data-testid={'subnav-items'}
        className="collapsible-subnav__items"
        id="collapsed-panel-nav">
        <DisclosureContent {...disclosureProps}>
          {visible && children}
        </DisclosureContent>
      </div>
    </StyledCollapsibleSubNav>
  );
};
